import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';
import { AccountCircle, Close, Login, Search } from '@mui/icons-material';
import { AuthPermissions } from 'skipify-types';

import { Button } from '../../../../components/core';
import { useDebounce } from '../../../../hooks/useDebounce';
import { Alert } from '../../../Paylinks/components/Alert';
import { DataTable, Column } from '../../../../common/DataTable';
import { Pagination } from '../../../../common/DataTable/Pagination';
import { MerchantAwareLink as RouterLink } from '../../../../components/core/MerchantAwareLink';
import { useMerchant, isMerchantIdParentPickerRow } from '../../../../contexts/MerchantContext';
import { useLazyGetMerchantQuery } from '../../../../store/modules/merchant/api';
import { MerchantAccess } from '../../../../store/modules/merchant/types';
import type { PaginatedResponse } from '../../../../store/modules/types';
import { CONFIG_MERCHANT_LIST_PAGE_SIZE, configurationPaths } from '../../../../util/constants';
import {
  buildUrlWithMerchantId,
  merchantAccessIdFromRow,
  type MerchantAccessApiRow,
} from '../../../../util/helpers';

const SEARCH_DEBOUNCE_MS = 300;

export const ChildMerchantsLandingView = () => {
  const {
    merchantId,
    canAccess,
    merchantAccess,
    parentMerchants,
    parentMerchantsTotalRecords,
    goBackToParentMerchantSelection,
    getPaginatedAccountMerchants,
    pendingChildMerchantLandingAfterParentPick,
  } = useMerchant();
  const [fetchMerchantForChildLogin] = useLazyGetMerchantQuery();
  const hasCreateMerchantsAccess = canAccess(AuthPermissions.CREATE_MERCHANTS);

  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CONFIG_MERCHANT_LIST_PAGE_SIZE);
  const [listResponse, setListResponse] = useState<PaginatedResponse<MerchantAccess> | null>(null);
  const [listLoading, setListLoading] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);

  const merchantIdIsParentPickerRow = isMerchantIdParentPickerRow(merchantId, parentMerchants);

  /** Paginated child list anchor: URL org on picker row / pending; else partner org for URL child. */
  const parentMerchantIdForChildren = useMemo(() => {
    if (!merchantId) return null;
    if (pendingChildMerchantLandingAfterParentPick || merchantIdIsParentPickerRow) {
      return merchantId;
    }
    const current = merchantAccess?.find((m) => m.merchantId === merchantId);
    return current?.partnerMerchantId ?? merchantId;
  }, [
    merchantId,
    merchantAccess,
    pendingChildMerchantLandingAfterParentPick,
    merchantIdIsParentPickerRow,
  ]);

  const applyDebouncedFilter = useDebounce((value: string) => {
    setFilterQuery(value);
  }, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchInput(value);
      if (value === '') {
        setFilterQuery('');
      } else {
        applyDebouncedFilter(value);
      }
    },
    [applyDebouncedFilter],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setFilterQuery('');
  }, []);

  useEffect(() => {
    setSearchInput('');
    setFilterQuery('');
  }, [merchantId]);

  useEffect(() => {
    setPage(1);
  }, [filterQuery, pageSize]);

  useEffect(() => {
    if (!parentMerchantIdForChildren) {
      setListResponse(null);
      setListLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setListLoading(true);
      try {
        const res = await getPaginatedAccountMerchants({
          pageNumber: page,
          pageSize,
          onlyChildMerchants: true,
          merchantId: parentMerchantIdForChildren,
          ...(filterQuery.trim() ? { merchantName: filterQuery.trim() } : {}),
        });
        if (!cancelled) {
          const current = merchantAccess?.find((m) => m.merchantId === merchantId);
          const partnerMerchantId = current?.partnerMerchantId ?? null;
          const filtered = res.data.filter((m) => {
            if (merchantId && m.merchantId === merchantId) return false;
            if (
              !pendingChildMerchantLandingAfterParentPick &&
              !merchantIdIsParentPickerRow &&
              partnerMerchantId &&
              m.merchantId === partnerMerchantId
            ) {
              return false;
            }
            return true;
          });
          setListResponse({ ...res, data: filtered });
        }
      } catch {
        if (!cancelled) {
          setListResponse(null);
        }
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    page,
    pageSize,
    filterQuery,
    getPaginatedAccountMerchants,
    parentMerchantIdForChildren,
    merchantId,
    merchantAccess,
    pendingChildMerchantLandingAfterParentPick,
    merchantIdIsParentPickerRow,
  ]);

  const pagedRows = useMemo((): MerchantAccess[] => {
    const raw = listResponse?.data ?? [];
    return raw.map((r) => {
      const id = merchantAccessIdFromRow(r as MerchantAccessApiRow);
      return id ? { ...(r as MerchantAccess), merchantId: id } : (r as MerchantAccess);
    });
  }, [listResponse?.data]);
  const totalRecords = listResponse?.metadata.totalRecords ?? 0;
  const pageCount = Math.max(1, listResponse?.metadata.totalPages ?? 1);
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const openChildPortal = useCallback((childMerchantId: string) => {
    const path = buildUrlWithMerchantId(configurationPaths.SETTINGS, childMerchantId);
    window.open(new URL(path, window.location.origin).href, '_blank', 'noopener,noreferrer');
  }, []);

  const loginToChildMerchant = useCallback(
    async (childMerchantId: string) => {
      try {
        await fetchMerchantForChildLogin(childMerchantId).unwrap();
      } catch {
        // Same as bootstrap in the new tab; still open so deep link can retry.
      }
      openChildPortal(childMerchantId);
    },
    [fetchMerchantForChildLogin, openChildPortal],
  );

  const columns: Column<MerchantAccess>[] = useMemo(
    () => [
      {
        headerName: 'Merchant name',
        field: 'name',
        minWidth: 280,
        renderCell: (row) => (
          <Typography variant="body2" color="text.primary">
            {row.name}
          </Typography>
        ),
      },
      {
        headerName: 'Actions',
        minWidth: 140,
        align: 'right',
        renderCell: (row) => (
          <Button
            theme="secondary"
            text="Login"
            startIcon={<Login sx={{ fontSize: 18 }} />}
            onClick={() => {
              const id = merchantAccessIdFromRow(row as MerchantAccessApiRow);
              if (id) void loginToChildMerchant(id);
            }}
            sx={{
              height: 40,
              minHeight: 40,
              px: 2,
            }}
          />
        ),
      },
    ],
    [loginToChildMerchant],
  );

  const showBackToParentList = (parentMerchantsTotalRecords ?? parentMerchants?.length ?? 0) > 1;

  return (
    <Stack spacing={3}>
      {showBackToParentList ? (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={goBackToParentMerchantSelection}
          sx={{ alignSelf: 'flex-start', cursor: 'pointer', fontWeight: 600 }}
        >
          Back to parent merchant list
        </Link>
      ) : null}
      <Alert severity="warning">
        <Stack spacing={1}>
          <Typography sx={{ fontSize: '14px' }}>
            You are viewing the parent merchant account. Select one of the child merchants to
            continue
            {hasCreateMerchantsAccess ? ', or create a new one.' : '.'}
          </Typography>
        </Stack>
      </Alert>

      <Stack gap={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            width: '100%',
            gap: 2,
            '& button': { width: { xs: '100%', md: '200px' } },
          }}
        >
          <Typography
            component="h2"
            variant="xl"
            fontWeight="semibold"
            sx={{ fontSize: { xs: '16px', md: '20px' } }}
          >
            Child merchants
          </Typography>
          {hasCreateMerchantsAccess ? (
            <Stack direction="row" spacing={8} sx={{ width: { xs: '100%', md: 'auto' } }}>
              <Button
                component={RouterLink}
                to={configurationPaths.CREATE_CHILD_MERCHANT}
                theme="primary"
                text="Create child merchant"
                startIcon={<AccountCircle sx={{ fontSize: 22 }} />}
                sx={{ px: 2 }}
              />
            </Stack>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
          <TextField
            key={merchantId ?? 'child-merchants-search'}
            fullWidth
            label="Search child merchants by name"
            value={searchInput}
            onChange={handleSearchChange}
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: -0.5 }}>
                    {searchInput.trim() ? (
                      <IconButton
                        aria-label="Clear search"
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearSearch();
                        }}
                        sx={{
                          cursor: 'pointer',
                          color: 'text.secondary',
                          p: 0.75,
                        }}
                      >
                        <Close sx={{ fontSize: 20 }} />
                      </IconButton>
                    ) : null}
                    <IconButton
                      aria-hidden
                      edge="end"
                      tabIndex={-1}
                      disableRipple
                      onClick={(e) => {
                        const input = e.currentTarget
                          .closest('.MuiInputBase-root')
                          ?.querySelector('input');
                        input?.focus();
                      }}
                      sx={{
                        px: [1.5, 3.5],
                        backgroundColor: 'link.main',
                        color: 'link.contrastText',
                        py: 1,
                        borderRadius: '100px',
                        mr: -1.2,
                        cursor: 'pointer',
                      }}
                    >
                      <Search />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: 'background.default',
              borderRadius: '51px',
              '& input': {
                pr: 2,
                py: 1.5,
              },
              '& .MuiInputBase-root': {
                borderRadius: '51px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.400',
                px: 2,
                fontSize: (theme) => theme.typography.pxToRem(17.7),
              },
              '& .MuiInputLabel-root': {
                ml: 1,
                lineHeight: '20px',
                color: 'text.primary',
                maxWidth: ['calc(100% - 80px)', 'calc(100% - 120px)'],
              },
            }}
          />
        </Box>
      </Stack>

      <DataTable<MerchantAccess>
        columns={columns}
        rows={pagedRows}
        getRowId={(row) => row.merchantId}
        isLoading={listLoading}
        pageSize={pageSize}
        tableHeight="min(480px, 60vh)"
        noRowsMessage="No child merchants found. Try adjusting your search or create a new one."
        tableRef={tableRef}
        renderPagination={() => (
          <Pagination
            itemCount={pagedRows.length}
            itemTotal={totalRecords}
            pageCount={pageCount}
            onPageChange={(_, p) => {
              setPage(p);
              tableRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            page={safePage}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPage(1);
              setPageSize(newPageSize);
            }}
            disabled={listLoading}
            pageSizeOptions={[10, 20, 30, 40, 50]}
          />
        )}
      />
    </Stack>
  );
};
